import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Upload, Building } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import logo from "@/assets/logo.png";

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [profile, setProfile] = useState({
    business_name: "",
    business_email: "",
    business_phone: "",
    business_address: "",
    business_logo_url: "",
    tax_id: "",
    website: "",
    invoice_prefix: "INV",
    invoice_notes: "",
    payment_terms: "Due upon receipt",
    currency: "RM",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/");
        return;
      }

      const { data, error } = await supabase
        .from("business_profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (data) {
        setProfile(data);
      }
    } catch (error: any) {
      toast({
        title: "Error loading profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileName = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("invoices")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Generate signed URL (valid for 1 year) for business logo
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from("invoices")
        .createSignedUrl(fileName, 31536000);

      if (signedUrlError) throw signedUrlError;
      const publicUrl = signedUrlData.signedUrl;

      setProfile({ ...profile, business_logo_url: publicUrl });
      
      toast({
        title: "Logo uploaded!",
        description: "Your business logo has been uploaded successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error uploading logo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("business_profiles")
        .upsert({
          user_id: user.id,
          ...profile,
        });

      if (error) throw error;

      toast({
        title: "Profile saved!",
        description: "Your business profile has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error saving profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logo} alt="SnapBill" className="h-8 md:h-10 w-8 md:w-10" />
            <div className="hidden sm:block">
              <h1 className="font-display font-bold text-lg md:text-xl">SnapBill</h1>
              <p className="text-xs text-muted-foreground">Snap. Bill. Paid.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} className="hidden md:flex">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} className="md:hidden">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-4 md:py-8 px-4 mb-20 md:mb-8">
        <div className="max-w-3xl mx-auto space-y-6 md:space-y-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">Invoice Settings</h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Customize your business details to appear on all your invoices
            </p>
          </div>

          {loading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Business Information
                  </CardTitle>
                  <CardDescription>
                    This information will appear on your invoices
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      placeholder="Your Company Ltd."
                      value={profile.business_name}
                      onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_email">Business Email</Label>
                      <Input
                        id="business_email"
                        type="email"
                        placeholder="business@example.com"
                        value={profile.business_email}
                        onChange={(e) => setProfile({ ...profile, business_email: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="business_phone">Business Phone</Label>
                      <Input
                        id="business_phone"
                        placeholder="+60123456789"
                        value={profile.business_phone}
                        onChange={(e) => setProfile({ ...profile, business_phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="business_address">Business Address</Label>
                    <Textarea
                      id="business_address"
                      placeholder="123 Main Street, City, State, Postal Code"
                      value={profile.business_address}
                      onChange={(e) => setProfile({ ...profile, business_address: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tax_id">Tax ID / Registration Number</Label>
                      <Input
                        id="tax_id"
                        placeholder="123456789"
                        value={profile.tax_id}
                        onChange={(e) => setProfile({ ...profile, tax_id: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        placeholder="https://yourwebsite.com"
                        value={profile.website}
                        onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="logo">Business Logo</Label>
                    <div className="flex items-center gap-4">
                      {profile.business_logo_url && (
                        <img 
                          src={profile.business_logo_url} 
                          alt="Business logo" 
                          className="h-16 w-16 object-contain border rounded"
                        />
                      )}
                      <div className="flex-1">
                        <Input
                          id="logo"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                          className="cursor-pointer"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Upload your business logo (PNG, JPG, or SVG)
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Invoice Customization</CardTitle>
                  <CardDescription>
                    Customize how your invoices are formatted
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_prefix">Invoice Number Prefix</Label>
                      <Input
                        id="invoice_prefix"
                        placeholder="INV"
                        value={profile.invoice_prefix}
                        onChange={(e) => setProfile({ ...profile, invoice_prefix: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Example: {profile.invoice_prefix || "INV"}-{Date.now()}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="currency">Currency</Label>
                      <Input
                        id="currency"
                        placeholder="RM"
                        value={profile.currency}
                        onChange={(e) => setProfile({ ...profile, currency: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="payment_terms">Payment Terms</Label>
                    <Input
                      id="payment_terms"
                      placeholder="Due upon receipt"
                      value={profile.payment_terms}
                      onChange={(e) => setProfile({ ...profile, payment_terms: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invoice_notes">Invoice Footer Notes</Label>
                    <Textarea
                      id="invoice_notes"
                      placeholder="Thank you for your business! Payment terms: Net 30 days."
                      value={profile.invoice_notes}
                      onChange={(e) => setProfile({ ...profile, invoice_notes: e.target.value })}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      This will appear at the bottom of your invoices
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button 
                  onClick={handleSave} 
                  disabled={saving || !profile.business_name}
                  className="bg-gradient-accent"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Settings;
